import { env } from '@/lib/env'
import {
  accessTokenExpired,
  clearTokens,
  getTokens,
  setTokens,
} from '@/lib/auth-tokens'
import { getActiveTenantId } from './tenant-view-store'

/**
 * App-wide HTTP client.
 *
 * Auth transport: bearer-header only. Go's JWTMiddleware accepts the
 * Authorization header exclusively, so React keeps the access +
 * refresh tokens client-side (see `lib/auth-tokens.ts`) and attaches
 * `Authorization: Bearer <token>` on every request.
 *
 * 401 handling: when an authenticated request comes back 401 we make
 * one attempt to refresh the access token via `POST /auth/refresh`,
 * then replay the original request with the new token. If refresh
 * itself 401s the analyst's session is dead — we clear the local
 * store and let the React Router auth gate redirect to /login.
 *
 * Throws `ApiError` on non-2xx so React Query's `error` branch is
 * structured. Returns `unknown` from `apiJson` — callers MUST
 * validate with zod or a type assertion. Don't add `<T>` to fake
 * type safety.
 */

export class ApiError extends Error {
  readonly status: number
  readonly code: string | undefined
  readonly details: unknown
  /** Server request / correlation id (error body `request_id` or X-Request-ID header), for support. */
  readonly requestId: string | undefined

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: unknown,
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.requestId = requestId
  }
}

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  searchParams?: Record<string, string | number | boolean | undefined>
  /** Set true on the auth endpoints themselves so we don't recurse on
   *  401 (a failed refresh shouldn't try to refresh again). */
  skipAuthRefresh?: boolean
  /** Set true on the login + refresh endpoints so we don't attach
   *  a stale bearer header to a credential-bearing call. */
  skipBearer?: boolean
}

function buildUrl(path: string, searchParams?: ApiOptions['searchParams']): string {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${base}${cleanPath}`, window.location.origin)
  if (searchParams) {
    for (const [k, v] of Object.entries(searchParams)) {
      if (v === undefined || v === null) continue
      url.searchParams.set(k, String(v))
    }
  }
  return url.pathname + url.search
}

// Single-flight refresh: when many requests 401 at once (typical when
// a session expires mid-screen) only one of them does the refresh; the
// rest await the resolved promise and reuse its outcome.
let refreshInFlight: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    const tokens = getTokens()
    if (!tokens) return false
    try {
      const url = buildUrl('/auth/refresh')
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: tokens.refresh_token }),
      })
      if (!res.ok) {
        clearTokens()
        return false
      }
      const body = (await res.json()) as {
        access_token: string
        refresh_token: string
        expires_in: number
        token_type?: string
      }
      setTokens({
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        expires_in: body.expires_in,
      })
      return true
    } catch {
      clearTokens()
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/**
 * Returns true when the path should NOT receive the target-tenant-id header.
 *
 * Excluded paths:
 *   /auth/*          — token exchange; no tenant context needed
 *   /me              — session auth sub-paths; no tenant context needed
 *   /platform/*      — platform-management routes get NO AUTO tenant header here;
 *                      platform/api.ts passes an explicit targetTenantHeader(targetTenantId)
 *                      on each call instead, so platform callers can specify exactly which
 *                      tenant they are managing. The auto-injection is suppressed here so
 *                      the ambient tenant-view store never fires on /platform/* paths.
 *   /rules/schema/*  — field catalogue and other schema-metadata endpoints are
 *                      tenant-agnostic static metadata. In tenant-view mode the ambient
 *                      tenant-view store would inject a target-tenant-id header which the
 *                      backend would reject with 403 (non-tenant-scoped endpoint). Exclude
 *                      the whole /rules/schema prefix to prevent a silent wizard fallback.
 *
 * Two-header models in use:
 *   AUTO  — this function injects the store-read tenant id for operational paths
 *            (alerts, cases, customers, transactions, screening, lists, reports,
 *            analytics, sars, audit, webhooks, branches, notifications, etc.)
 *   EXPLICIT — platform/api.ts calls pass targetTenantHeader(targetTenantId) directly
 *              and those headers arrive here via the caller-supplied `headers` object,
 *              which is merged AFTER tenantHeader below (caller headers win). Suppressing
 *              auto-injection for /platform/* avoids a spurious double-header where both
 *              a wrong ambient store value and the correct explicit value compete.
 *
 * Security: the backend independently validates the header against the
 * caller's JWT. This exclusion list is belt-and-suspenders only.
 *
 * Exported for unit tests only — do not use this from feature modules.
 * The injection is handled automatically inside the `api` function.
 */
export function isTenantHeaderExcludedPath(path: string): boolean {
  const clean = path.startsWith('/') ? path : `/${path}`
  return (
    clean === '/me' ||
    clean.startsWith('/auth/') ||
    clean.startsWith('/platform/') ||
    clean.startsWith('/rules/schema')
  )
}

export async function api(path: string, options: ApiOptions = {}): Promise<Response> {
  const {
    body,
    searchParams,
    headers,
    skipAuthRefresh,
    skipBearer,
    ...rest
  } = options
  const url = buildUrl(path, searchParams)

  const tokens = getTokens()
  // Proactive refresh: if we have a refresh token and the access
  // token is past skew, refresh before issuing the request. Skipped
  // on the auth endpoints themselves (they manage their own tokens).
  if (
    !skipBearer &&
    !skipAuthRefresh &&
    tokens &&
    accessTokenExpired() &&
    tokens.refresh_token
  ) {
    await refreshOnce()
  }

  const sendTokens = getTokens()

  // AUTO tenant-id injection for operational endpoints only.
  // /platform/* is excluded — those calls carry an EXPLICIT target-tenant-id
  // supplied by platform/api.ts (merged via the caller `headers` below).
  // /auth/* and /me are excluded because they carry no tenant context.
  //
  // Security: the backend validates the header against the actor's JWT.
  // A non-platform-admin receiving this header gets a 403 server-side.
  // This injection does not bypass any backend check.
  const activeTenantId = !isTenantHeaderExcludedPath(path)
    ? getActiveTenantId()
    : null
  const tenantHeader: Record<string, string> = activeTenantId
    ? { 'target-tenant-id': activeTenantId }
    : {}

  const init: RequestInit = {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(sendTokens && !skipBearer
        ? { Authorization: `Bearer ${sendTokens.access_token}` }
        : {}),
      // Tenant header before caller-supplied headers so callers can override.
      ...tenantHeader,
      ...headers,
    },
  }
  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }

  let res = await fetch(url, init)

  // Reactive 401 retry: if the request comes back unauthorised and we
  // weren't already in an auth-flow call, try a single refresh +
  // replay. Anything that 401s twice is genuinely logged out.
  if (res.status === 401 && !skipAuthRefresh && !skipBearer) {
    const refreshed = await refreshOnce()
    if (refreshed) {
      const replayTokens = getTokens()
      if (replayTokens) {
        const replayInit: RequestInit = {
          ...init,
          headers: {
            ...(init.headers as Record<string, string> | undefined),
            Authorization: `Bearer ${replayTokens.access_token}`,
          },
        }
        res = await fetch(url, replayInit)
      }
    }
  }

  // `Response.ok` is true for the ENTIRE 200-299 range, including 207
  // Multi-Status. Today every 207-emitting Go endpoint this SPA
  // actually calls (`POST /alerts/bulk/close`) uses 207 as a
  // whole-batch-rejection signal — see `internal/cases/bulk`'s doc
  // comment: if ANY item in the batch fails validation, NOTHING is
  // written and the handler answers 207 with a per-item results
  // ledger instead of the requested count. Treating 207 as success
  // would tell an operator a rejected batch succeeded, so every 207
  // is treated as a failure here, uniformly, for every caller — not
  // just alerts bulk-close. This is a deliberate blast-radius
  // decision: a local-only fix (e.g. inside bulkCloseAlerts) would
  // leave the same trap armed for the next 207-emitting endpoint this
  // app wires up (`internal/cases/bulk`'s `/cases/bulk/assign` +
  // `/cases/bulk/status` follow the same whole-batch contract and
  // aren't called from `web/` yet).
  //
  // Known exception, not currently reachable from this app:
  // `POST /cases/{id}/alerts` (`CasesHandler.LinkAlerts` in
  // `internal/api/cases_handler.go`) uses 207 for a GENUINELY partial
  // success ("the rest still committed" — non-fatal per-alert
  // failures like already-linked/not-found don't block the alerts
  // that DID link). If a future caller wires that endpoint up, do NOT
  // reuse this blanket throw for it — it needs its own handling that
  // reads the 2xx range and treats `results[].success === false`
  // entries as partial info, not a full-batch failure. The endpoint
  // this SPA actually uses for linking (`/alerts/bulk/link-to-case`)
  // always returns 200 with a `skipped` count instead, so this
  // exception doesn't affect any current caller.
  if (!res.ok || res.status === 207) {
    // The platform's canonical error envelope is { error: { code, message, details } }.
    // Some older endpoints may return a flat { code, message } shape; we try the
    // nested envelope first and fall back to the flat shape so nothing silently
    // loses its error code during the migration window.
    type ErrorEnvelope = {
      error?: { code?: string; message?: string; details?: unknown; request_id?: string }
    }
    type FlatPayload = {
      code?: string
      message?: string
      details?: unknown
      request_id?: string
    }
    let raw: unknown = null
    try {
      raw = await res.clone().json()
    } catch {
      // Body wasn't JSON — fall through with null payload.
    }

    if (res.status === 207) {
      // 207 bodies are NOT the { error } envelope above — they're an
      // endpoint-specific success/failure ledger (e.g.
      // `{ affected, results }` for bulk-close). We don't have a
      // message/code to unwrap, so we synthesize a generic one and
      // preserve the raw ledger body on `details` so endpoint-specific
      // callers (e.g. `useBulkClose`) can read the structured per-item
      // results to explain the rejection to the operator.
      throw new ApiError(
        'Batch rejected — one or more items failed validation',
        207,
        'multi_status',
        raw,
      )
    }

    const typedRaw = raw as (ErrorEnvelope & FlatPayload) | null
    const errBody = typedRaw?.error ?? typedRaw
    throw new ApiError(
      errBody?.message ?? `${res.status} ${res.statusText}`,
      res.status,
      errBody?.code,
      errBody?.details,
      errBody?.request_id || res.headers.get('X-Request-ID') || undefined,
    )
  }

  return res
}

export async function apiJson<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const res = await api(path, options)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
